import React, {useEffect, useState} from 'react';
import {Card, CardContent, Box, Link, Typography} from '@mui/material';
import {
  ChevronDownDark,
  ChevronUpDark,
  Dot,
  HiristDot,
  Seprator,
} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useInView, useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {BottomSheet} from '@jobseeker/lotus/elements';
import {AppState, JobDetail} from '@jobseeker/common-core/types';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {useSelector} from 'react-redux';
import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const CompanyJobs: React.FC<{
  jobs: JobDetail[];
  companyName: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
}> = ({jobs, companyName, isExpanded = false, onToggle, templateInfo}) => {
  const isSmall = useIsCustomWidth('sm');
  const [open, setOpen] = useState<boolean>(false);
  const router = useRouter();
  const jobInfo: JB = useSelector((state: AppState) => state.jobDetail);

  const showAll = isExpanded;
  const toggleShowAll = onToggle || (() => {});

  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const [companyJobsTracked, setCompanyJobsTracked] = useState(false);

  useEffect(() => {
    if (isInView && !companyJobsTracked && jobs && jobs?.length > 0) {
      TrackEventUBA('sectionView', 'view', {
        ref: router.query.ref || '',
        pageName: 'JD',
        jobID: jobInfo.id,
        page: 'JD',
        templateID: templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: jobInfo?.companyData?.companyId,
        centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
        companyName: jobInfo?.companyData?.companyName,
        section: 'SimilarJobs',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setCompanyJobsTracked(true);
    }
  }, [isInView, companyJobsTracked]);

  const showJobs = (show?: boolean) => {
    const vis = isSmall ? 2 : 3;
    const displayedJobs = showAll || show ? jobs : jobs.slice(0, vis);

    return (
      <Box ref={ref}>
        <Card
          variant="outlined"
          sx={{
            border: 'none',
            backgroundColor: open ? '#fff' : '#F9FAFB',
          }}
        >
          <CardContent
            sx={{
              p: 2,
              px: 1.5,
              '&:last-child': {
                pb: 1.5,
              },
            }}
          >
            {displayedJobs.map((b, idx) => (
              <>
                <Box key={idx}>
                  <Box
                    component={'a'}
                    mb={'6px'}
                    href={`/j/${b.title}-${b?.id}`}
                    onClick={() => {
                      TrackEventUBA('jdPageClick', 'click', {
                        ref: router.query.ref || '',
                        pageName: 'Job Description Page',
                        jobID: jobInfo?.id,
                        jobVersion: '',
                        jobRef: getKeyFromPath('ref') || '',
                        jobPos: router.query.jobPos || '',
                        page: 'JD',
                        isBranded: 'Yes',
                        section: 'SimilarJobs',
                        sessionUsername: sessionStorage.getItem('userId') || '',
                        typeClick: `SimilarJobs-${jobInfo?.id}-${b?.id}`,
                        companyID: jobInfo?.companyData?.companyId,
                        centralCompanyID:
                          jobInfo?.recruiter?.centralizedCompanyId,
                        companyName: jobInfo?.companyData?.companyName,
                        templateID: templateInfo?.templateNo,
                      });
                    }}
                    target="_blank"
                    sx={{textDecoration: 'none', color: '#333'}}
                  >
                    <Typography
                      fontSize={isAppIimjobs ? 12 : 14}
                      fontWeight={400}
                      lineHeight={'16px'}
                    >
                      {b.title}
                    </Typography>
                    <Box
                      display={'flex'}
                      alignItems={'center'}
                      gap={0.5}
                      mt={0.75}
                    >
                      <Typography
                        fontSize={isAppIimjobs ? 12 : 12}
                        fontWeight={400}
                        color={isAppIimjobs ? '#7E7E7E' : '#000'}
                        lineHeight={'12px'}
                      >
                        {`${b?.min}-${b?.max} yrs`}
                      </Typography>
                      <Image
                        src={isAppIimjobs ? Dot : HiristDot}
                        alt="dot"
                        height={3}
                        width={3}
                        style={{marginLeft: 4, marginRight: 4}}
                      />
                      <Typography
                        fontSize={isAppIimjobs ? 12 : 12}
                        fontWeight={400}
                        color={isAppIimjobs ? '#7E7E7E' : '#000'}
                        lineHeight={'12px'}
                      >
                        {b?.location.length > 3
                          ? 'Multiple Locations'
                          : b?.location?.map(l => l.name).join('/')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {idx != displayedJobs.length - 1 ? (
                  <Image
                    src={Seprator}
                    height={10}
                    width={600}
                    style={{
                      width: open ? 'calc(100% + 72px)' : '100%',
                      marginLeft: open ? -16 : 0,
                      paddingBottom: '0px',
                    }}
                    alt="sep"
                  />
                ) : (
                  <></>
                )}
              </>
            ))}
            {jobs.length > vis && !show && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => {
                  TrackEventUBA('jdPageClick', 'click', {
                    ref: router.query.ref || '',
                    pageName: 'Job Description Page',
                    jobID: jobInfo?.id,
                    jobVersion: '',
                    jobRef: getKeyFromPath('ref') || '',
                    jobPos: router.query.jobPos || '',
                    page: 'JD',
                    isBranded: 'Yes',
                    section: 'SimilarJobs',
                    sessionUsername: sessionStorage.getItem('userId') || '',
                    typeClick: `SimilarJobs-${
                      showAll ? 'ShowLess' : 'ShowMore'
                    }`,
                    companyID: jobInfo?.companyData?.companyId,
                    centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
                    companyName: jobInfo?.companyData?.companyName,
                    templateID: templateInfo?.templateNo,
                  });
                  if (isSmall) {
                    setOpen(true);
                  } else {
                    toggleShowAll();
                  }
                }}
                sx={{cursor: 'pointer', mt: '14px'}}
              >
                <Link
                  color="primary"
                  underline="none"
                  letterSpacing="0.48px"
                  lineHeight="18px"
                  fontSize={isAppIimjobs ? 12 : 14}
                  fontWeight={isAppIimjobs ? 500 : 600}
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </Link>
                {!isSmall && (
                  <Image
                    src={!showAll ? ChevronDownDark : ChevronUpDark}
                    alt="chevron"
                    height={18}
                    width={18}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
      {' '}
      <BottomSheet
        open={open}
        title={`Discover similar roles at ${companyName}`}
        onDismiss={() => {
          setOpen(false);
          TrackEventUBA('jdPageClick', 'click', {
            ref: router.query.ref || '',
            pageName: 'Job Description Page',
            jobID: jobInfo?.id,
            jobVersion: '',
            jobRef: getKeyFromPath('ref') || '',
            jobPos: router.query.jobPos || '',
            page: 'JD',
            isBranded: 'Yes',
            section: 'SimilarJobs',
            sessionUsername: sessionStorage.getItem('userId') || '',
            typeClick: 'SimilarJobs-ShowLess',
            companyID: jobInfo?.companyData?.companyId,
            centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
            companyName: jobInfo?.companyData?.companyName,
            templateID: templateInfo?.templateNo,
          });
        }}
      >
        {showJobs(true)}
      </BottomSheet>
      {showJobs()}
    </>
  );
};

export default CompanyJobs;
